import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { Copy, Check, X } from 'lucide-react'
import buildInfo from '@/build-info.json'
import { ScreenshotDetector } from '@/lib/native/screenshot-detector'

/**
 * Shows the running app version for a few seconds after a screenshot, so a
 * tester can copy it into the /tester feedback form. Native-only: the trigger
 * (ScreenshotDetector) has no web implementation, and the whole point is to
 * surface the *native* build number that the static suite can't read.
 *
 * This does NOT put the version into the screenshot itself — that's impossible
 * (the OS captures the frame before the app is notified). It's a copy-paste
 * bridge for manual entry.
 */

function webHash(): string {
  // buildId looks like "0.1.0-365fa87-1783685794040" → the git short hash.
  const id = (buildInfo as { buildId?: string })?.buildId || ''
  const parts = id.split('-')
  return parts[1] || id || 'dev'
}

export function ScreenshotVersionHint() {
  const [version, setVersion] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let handle: { remove: () => void } | undefined
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const buildVersionString = async (): Promise<string> => {
      try {
        const info = await CapacitorApp.getInfo()
        return `ZeroSnag ${info.version} (${info.build}) · web ${webHash()}`
      } catch {
        return `ZeroSnag · web ${webHash()}`
      }
    }

    const onShot = async () => {
      const v = await buildVersionString()
      if (cancelled) return
      setCopied(false)
      setVersion(v)
      if (hideTimer) clearTimeout(hideTimer)
      hideTimer = setTimeout(() => setVersion(null), 12000)
    }

    ScreenshotDetector.addListener('screenshotTaken', () => void onShot())
      .then((h) => { if (cancelled) void h.remove(); else handle = h })
      .catch(() => {})

    return () => {
      cancelled = true
      if (hideTimer) clearTimeout(hideTimer)
      try { handle?.remove() } catch { /* noop */ }
    }
  }, [])

  const copy = async () => {
    if (!version) return
    try {
      await navigator.clipboard.writeText(version)
      setCopied(true)
    } catch {
      // Clipboard blocked → the text is selectable in the banner as a fallback.
      setCopied(false)
    }
  }

  if (!version) return null

  return (
    <div
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      className="fixed inset-x-0 z-[9999] flex justify-center px-4 pointer-events-none"
    >
      <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/85 text-white shadow-lg px-3 py-2 text-xs max-w-[92vw]">
        <span className="opacity-70 shrink-0">📸</span>
        <span className="font-mono select-all truncate">{version}</span>
        <button
          type="button"
          onClick={() => void copy()}
          className="ml-1 shrink-0 flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 px-2 py-1"
          aria-label="App-Version kopieren"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Kopiert' : 'Kopieren'}
        </button>
        <button
          type="button"
          onClick={() => setVersion(null)}
          className="shrink-0 opacity-60 hover:opacity-100"
          aria-label="Schließen"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
